(function () {
  'use strict';

  const TAG = 'curator-invitation';
  const AVAILABILITY_EVENT = 'airx:curator:availability';
  const startedGreetings = window.__airxCuratorInvitationStarted || new Set();
  const visibleInvitations = window.__airxCuratorInvitationsVisible || new Set();
  window.__airxCuratorInvitationStarted = startedGreetings;
  window.__airxCuratorInvitationsVisible = visibleInvitations;

  function syncLauncherVisibility() {
    document.documentElement.classList.toggle('airx-curator-invitation-visible', visibleInvitations.size > 0);
  }

  class CuratorInvitation extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.greeting = this.querySelector('[data-curator-inline-greeting]');
      this.output = this.querySelector('[data-curator-inline-greeting-output]');
      this.caret = this.querySelector('[data-curator-inline-caret]');
      this.capabilities = Array.from(this.querySelectorAll('[data-curator-inline-capability]'));
      this.closing = this.querySelector('[data-curator-inline-closing]');
      this.statusEl = this.querySelector('[data-curator-inline-status]');
      this.statusText = this.querySelector('[data-curator-inline-status-text]');
      this.copy = this.greeting ? String(this.greeting.dataset.curatorGreetingText || '').trim() : '';
      this.key = this.dataset.sectionKey || this.copy;
      this._complete = false;
      this._started = false;

      this._onAvailability = (event) => this.applyAvailability(event && event.detail);
      document.addEventListener(AVAILABILITY_EVENT, this._onAvailability);
      this._onNetworkOnline = () => this.requestAvailabilityRefresh(true);
      window.addEventListener('online', this._onNetworkOnline);
      this.applyAvailability(window.__airxCuratorAvailability || { state: 'checking' });
      if (window.customElements && typeof window.customElements.whenDefined === 'function') {
        window.customElements.whenDefined('sales-curator').then(() => {
          if (!this.isConnected) return;
          const availability = window.__airxCuratorAvailability;
          if (availability) this.applyAvailability(availability);
          const stale = !availability || Date.now() - Number(availability.at || 0) > 30000;
          if (stale) this.requestAvailabilityRefresh(false);
        });
      }

      if ('IntersectionObserver' in window) {
        this._presenceObserver = new IntersectionObserver((entries) => {
          const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.06);
          if (visible) visibleInvitations.add(this.key);
          else visibleInvitations.delete(this.key);
          syncLauncherVisibility();
        }, { threshold: [0, 0.06] });
        this._presenceObserver.observe(this);
      }

      if (!this.greeting || !this.output || !this.copy) return;
      if (startedGreetings.has(this.key)) {
        this.completeGreeting(false);
        return;
      }

      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const themeMotionOff = !document.documentElement.classList.contains('motion');
      if (reducedMotion || themeMotionOff || !('IntersectionObserver' in window)) {
        this.startGreeting(true);
        return;
      }

      this.prepareGreeting();

      this._observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.08);
        if (!visible) return;
        this._observer.disconnect();
        this._observer = null;
        this.startGreeting(false);
      }, {
        threshold: [0.08, 0.2],
        rootMargin: '0px 0px -6% 0px',
      });
      this._observer.observe(this.greeting);
    }

    disconnectedCallback() {
      this._connected = false;
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      if (this._presenceObserver) this._presenceObserver.disconnect();
      this._presenceObserver = null;
      if (this._onAvailability) document.removeEventListener(AVAILABILITY_EVENT, this._onAvailability);
      this._onAvailability = null;
      if (this._onNetworkOnline) window.removeEventListener('online', this._onNetworkOnline);
      this._onNetworkOnline = null;
      visibleInvitations.delete(this.key);
      syncLauncherVisibility();
      if (this._timer) window.clearTimeout(this._timer);
      this._timer = null;
      if (this._started && !this._complete) this.completeGreeting(false);
    }

    applyAvailability(detail) {
      if (!this.statusEl) return;
      const state = detail && /^(online|offline|checking)$/.test(detail.state)
        ? detail.state
        : 'checking';
      this.statusEl.dataset.state = state;
      if (!this.statusText) return;
      const label = state === 'online'
        ? this.statusEl.dataset.onlineText
        : state === 'offline'
          ? this.statusEl.dataset.offlineText
          : this.statusEl.dataset.checkingText;
      this.statusText.textContent = label || (state === 'online' ? 'Online' : state === 'offline' ? 'Offline' : 'Checking availability…');
    }

    requestAvailabilityRefresh(force) {
      const curator = document.querySelector('sales-curator');
      if (!curator || typeof curator.checkHealth !== 'function') return;
      const availability = window.__airxCuratorAvailability;
      const freshOnline = availability && availability.state === 'online' && Date.now() - Number(availability.at || 0) < 30000;
      if (force || !freshOnline) curator.checkHealth(true);
    }

    prepareGreeting() {
      if (!this.greeting || !this.output) return;
      this.output.textContent = '';
      for (const capability of this.capabilities) {
        capability.hidden = true;
        capability.dataset.state = 'waiting';
      }
      if (this.closing) {
        this.closing.hidden = true;
        this.closing.dataset.state = 'waiting';
      }
      this.greeting.dataset.state = 'waiting';
      this.greeting.setAttribute('aria-busy', 'false');
      if (this.caret) this.caret.hidden = true;
    }

    startGreeting(immediate) {
      if (this._started || this._complete) return;
      this._started = true;
      startedGreetings.add(this.key);
      if (immediate) {
        this.completeGreeting(false);
        return;
      }

      const tokens = this.copy.match(/\S+\s*/g) || [this.copy];
      let index = 0;
      this.output.textContent = '';
      this.greeting.dataset.state = 'typing';
      this.greeting.setAttribute('aria-busy', 'true');
      if (this.caret) this.caret.hidden = false;

      const revealNext = () => {
        if (!this.isConnected || this._complete) return;
        this.output.appendChild(document.createTextNode(tokens[index]));
        const word = tokens[index].trim();
        index += 1;
        if (index >= tokens.length) {
          this._timer = window.setTimeout(() => this.revealCapabilities(0), 220);
          return;
        }
        const pause = /[.!?][”’"']?$/.test(word)
          ? 190
          : /[,;:][”’"']?$/.test(word)
            ? 115
            : 52 + Math.round(Math.random() * 30);
        this._timer = window.setTimeout(revealNext, pause);
      };

      this._timer = window.setTimeout(revealNext, 260);
    }

    revealCapabilities(index) {
      if (!this.isConnected || this._complete) return;
      this.greeting.dataset.state = 'introducing';
      if (index < this.capabilities.length) {
        const capability = this.capabilities[index];
        capability.hidden = false;
        capability.dataset.state = 'entering';
        window.requestAnimationFrame(() => {
          if (capability.isConnected) capability.dataset.state = 'visible';
        });
        this._timer = window.setTimeout(() => this.revealCapabilities(index + 1), 460);
        return;
      }

      if (this.closing) {
        this.closing.hidden = false;
        this.closing.dataset.state = 'entering';
        window.requestAnimationFrame(() => {
          if (this.closing && this.closing.isConnected) this.closing.dataset.state = 'visible';
        });
        this._timer = window.setTimeout(() => this.completeGreeting(false), 520);
        return;
      }
      this.completeGreeting(false);
    }

    completeGreeting(markStarted) {
      if (!this.greeting || !this.output || !this.copy) return;
      if (markStarted !== false) startedGreetings.add(this.key);
      if (this._observer) this._observer.disconnect();
      this._observer = null;
      if (this._timer) window.clearTimeout(this._timer);
      this._timer = null;
      this._started = true;
      this._complete = true;
      this.output.textContent = this.copy;
      for (const capability of this.capabilities) {
        capability.hidden = false;
        capability.dataset.state = 'complete';
      }
      if (this.closing) {
        this.closing.hidden = false;
        this.closing.dataset.state = 'complete';
      }
      this.greeting.dataset.state = 'complete';
      this.greeting.setAttribute('aria-busy', 'false');
      if (this.caret) this.caret.hidden = true;
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, CuratorInvitation);
})();
